import React, { useState } from "react";
import clsx from "clsx";
import ProgressBar from "../progress-bar";
import CopyIcon from "../../assests/CopyIcon.svg";
import { useChaseMenu } from "../../store/use-chase-menu";
import { toast } from "react-toastify";
import moment from "moment";
import CustomButton from "../customs/custom-button";
import axiosInstance from "../../utils/axiosInstance";
import { ParseErrors } from "../../utils/ParseErrors";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRightLong,
  faArrowUpRightFromSquare,
  faFileLines,
  faPhone,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import {
  LAPSE_REASONS,
  QUOTE_LINE_FIELDS,
  QUOTE_SLOTS,
  QUOTE_SLOT_LABEL,
  money,
} from "../../utils/create-chase.constants";
import {
  attemptsAction,
  autoPotActions,
  autoPotFailed,
  canTakePayment,
  daysToRenewal,
  fileSize,
  inAutoPot,
  payLink,
  paymentLabel,
  retryDue,
  retryRenewedAction,
  rowActions,
  statusNote,
} from "../../utils/chase-actions.constants";
import useChaseAction from "../../utils/use-chase-action";

interface IChaseDropDown {
  UpdateItemData: () => void;
}

/** Shared look for the inline fields sitting under the timeline. */
const fieldClass =
  "mt-3 border border-input-border rounded-xl bg-input-bg text-text-input text-[14px] px-3.5 h-[44px] w-full outline-none transition-colors duration-200 hover:border-indigo-400/40 focus:border-indigo-400/60 focus:ring-1 focus:ring-indigo-500/25 disabled:opacity-60";

const ChaseDropMenu = ({ UpdateItemData }: IChaseDropDown) => {
  const { initialValues } = useChaseMenu();
  const { chase } = initialValues;
  const paymentLink = payLink(
    chase?.policyReferenceNumber,
    chase?.versionReferenceNumber,
  );
  const [logs, setLogs] = useState({
    data: [...chase?.logs],
    totalCount: chase?.logs?.length,
  });
  const [loading, setLoading] = useState(false);
  // The renewal document whose link is being fetched, if any.
  const [openingDoc, setOpeningDoc] = useState<number | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [page, setPage] = useState(0);
  const [limit] = useState(10);

  // ---------------------------------------------- [ COPY TO CLIPBOARD ] --------------------------------------------------------------
  const CopytoClipboard = () => {
    navigator.clipboard.writeText(paymentLink);
    toast.success("Copied");
  };

  const loadMoreLogs = async () => {
    setIsFetching(true);
    try {
      const res = await axiosInstance.get(
        `/loan/find/logs/${chase?.id}?limit=${limit}&offset=${limit + page}`,
      );
      setPage(page + limit);
      setLogs((prevState) => ({
        data: [...prevState.data, ...res.data.data],
        totalCount: res.data.totalCount,
      }));
      setIsFetching(false);
    } catch (error) {
      console.log(error);
      setIsFetching(false);
    }
  };

  // ------------------------------- [ OPEN A RENEWAL DOCUMENT ] -------------------------------
  // The bucket is private and no link is stored, so one is signed on the click.
  const OpenDocument = async (id: number) => {
    setOpeningDoc(id);
    try {
      const res = await axiosInstance.get(`/chase/document/${id}`);
      window.open(res.data.link, "_blank");
    } catch (error) {
      toast.error(ParseErrors(error));
    } finally {
      setOpeningDoc(null);
    }
  };

  // ------------------------------- [ INLINE FIELD EDIT — LAPSE REASON, ATTEMPTS ] -------------------------------
  const UpdateChase = async (patch: Record<string, any>, success: string) => {
    setLoading(true);
    try {
      await axiosInstance.patch(`/chase/${chase?.id}`, patch);
      UpdateItemData();
      toast.success(success);
    } catch (error) {
      toast.error(ParseErrors(error));
    } finally {
      setLoading(false);
    }
  };

  // Same action the row's ⋮ menu offers, with the same confirmation.
  const { runAction, loading: actionLoading } = useChaseAction(
    chase?.id,
    UpdateItemData,
    chase?.versionReferenceNumber,
  );
  // The retry box carries its own Mark as Renewed, so don't offer a second one.
  const markRenewed = autoPotFailed(chase)
    ? undefined
    : rowActions(chase).find((action) => action.key === "renewed");

  // Newest first — a re-renewal adds to the pack rather than replacing it.
  const renewalDocuments = [...(chase?.renewalDocuments ?? [])].sort((a, b) =>
    String(b.createdAt).localeCompare(String(a.createdAt)),
  );

  // Invite slots in fixed order — Main first, then the alternatives.
  const quotes = [...(chase?.quotes ?? [])].sort(
    (a, b) => QUOTE_SLOTS.indexOf(a.slot) - QUOTE_SLOTS.indexOf(b.slot),
  );

  return (
    <td colSpan={10}>
      <div className="bg-surface-muted rounded-2xl border border-edge p-4 sm:p-5 my-3 animate-fade-in shadow-card smallTablet:sticky smallTablet:left-4 smallTablet:w-[calc(100vw-4rem)]">
        {/* AUTO PAYMENT ATTEMPT — the policy should collect itself; say whether it did. */}
        {inAutoPot(chase) ? (
          <div className="w-full bg-surface rounded-2xl border border-amber-400/40 p-5 sm:p-6 mb-4">
            <SectionLabel>Auto Payment Attempt</SectionLabel>
            <p className="mt-2 text-[13px] text-light-gray">
              Renews {moment(chase?.renewalDate).format("DD MMM YYYY")} —{" "}
              {chase?.paymentType === "instalments"
                ? "the Direct Debit"
                : "the renewal payment"}{" "}
              should have collected. Attempts so far:{" "}
              <span className="font-semibold text-heading-color">
                {chase?.paymentAttempts ?? 0}
              </span>
              .
            </p>
            <div className="flex flex-wrap gap-2 mt-4">
              {autoPotActions(chase).map((action) => (
                <CustomButton
                  key={action.key}
                  className={clsx(
                    "!h-[42px] !px-5 rounded-lg font-medium",
                    action.key !== "auto-renewed" &&
                      "border border-amber-400/50 text-amber-600 dark:text-amber-400 hover:bg-amber-500/10 transition-colors duration-200",
                  )}
                  variant={
                    action.key === "auto-renewed" ? "primary" : undefined
                  }
                  onClick={() => runAction(action)}
                  disabled={actionLoading}
                >
                  {action.label}
                </CustomButton>
              ))}
            </div>
          </div>
        ) : null}

        {/* AUTO POT, PAYMENT FAILED — the money has to be taken by hand now, and
            nothing chases it on its own, so this asks again every other day. */}
        {autoPotFailed(chase) ? (
          <div
            className={clsx(
              "w-full bg-surface rounded-2xl border p-5 sm:p-6 mb-4",
              retryDue(chase) ? "border-amber-400/40" : "border-edge",
            )}
          >
            <SectionLabel>Auto Pot — Payment Failed</SectionLabel>
            <p className="mt-2 text-[13px] text-light-gray">
              {retryDue(chase) ? "Try the payment again today. " : ""}
              Renews {moment(chase?.renewalDate).format("DD MMM YYYY")} —{" "}
              {daysToRenewal(chase)} days away. Attempts so far:{" "}
              <span className="font-semibold text-heading-color">
                {chase?.paymentAttempts ?? 0}
              </span>
              .
            </p>
            <div className="flex flex-wrap gap-2 mt-4">
              <CustomButton
                className="!h-[42px] !px-5 rounded-lg font-medium"
                variant="primary"
                onClick={() => runAction(retryRenewedAction)}
                disabled={actionLoading}
              >
                Mark as Renewed
              </CustomButton>
              {/* Asks for the new attempt count — the retry was tried, not paid. */}
              <CustomButton
                className="!h-[42px] !px-5 rounded-lg font-medium border border-amber-400/50 text-amber-600 dark:text-amber-400 hover:bg-amber-500/10 transition-colors duration-200"
                onClick={() => runAction(attemptsAction(chase))}
                disabled={actionLoading}
              >
                Update Payment
              </CustomButton>
            </div>
          </div>
        ) : null}

        <div className="w-full bg-surface rounded-2xl border border-edge p-5 sm:p-6">
          <div className="flex flex-col lg:flex-row items-start gap-6">
            <div className="w-full lg:flex-1 min-w-0">
              <div className="w-full flex items-center gap-2">
                <SectionLabel>Chase Timeline</SectionLabel>
                {chase?.callId ? (
                  <FontAwesomeIcon
                    icon={faPhone}
                    className="text-light-gray text-xs"
                  />
                ) : null}
              </div>
              <div className="pt-6 pb-2">
                <ProgressBar
                  timeline={chase?.timeline?.sort((a, b) => b.day - a.day)}
                />
              </div>
            </div>

            <div className="w-full lg:w-[260px] shrink-0">
              <div className="w-full h-fit rounded-xl px-4 py-3.5 bg-surface-elevated border border-edge">
                <p className="text-[11px] font-semibold text-light-gray uppercase tracking-[0.14em]">
                  Payment Link
                </p>
                <div className="flex items-center justify-between gap-3 mt-2">
                  <h1
                    className="text-[11px] text-blue font-bold cursor-pointer text-nowrap flex-1 overflow-hidden text-ellipsis"
                    onClick={() => window.open(paymentLink, "_blank")}
                  >
                    {paymentLink.replace(/^https?:\/\//, "")}
                  </h1>
                  <div className="shrink-0" onClick={CopytoClipboard}>
                    <div className="bg-active-background w-8 h-8 rounded-full flex justify-center items-center cursor-pointer border border-edge hover:border-indigo-400/40 transition-colors duration-200">
                      <img className="w-3 h-4" src={CopyIcon} alt="" />
                    </div>
                  </div>
                </div>
              </div>

              {canTakePayment(chase) ? (
                <CustomButton
                  className="!w-full !mt-2 !h-[42px]"
                  style={{ width: "100%" }}
                  variant="primary"
                  onClick={() => window.open(paymentLink, "_blank")}
                  disabled={loading}
                >
                  {paymentLabel(chase)}
                </CustomButton>
              ) : (
                // Settled one way or the other — say where it landed.
                <div className="mt-2 w-full rounded-xl border border-edge bg-surface-elevated px-4 py-3 flex items-center gap-2.5">
                  <span
                    className={clsx(
                      "h-2.5 w-2.5 rounded-full shrink-0",
                      statusNote(chase).dot,
                    )}
                  />
                  <p className="text-[14px] font-bold text-heading-color">
                    {statusNote(chase).label}
                  </p>
                </div>
              )}

              {/* Paid (or won back) — the agent closes it off as renewed. */}
              {markRenewed ? (
                <CustomButton
                  className="!w-full !mt-2 !h-[42px]"
                  style={{ width: "100%" }}
                  variant="primary"
                  onClick={() => runAction(markRenewed)}
                  disabled={actionLoading}
                >
                  {markRenewed.label}
                </CustomButton>
              ) : null}
            </div>
          </div>
        </div>

        {/* Why this policy isn't auto-renewing, and how many times we've tried
            to take the money — both saved the moment they're picked. */}
        <Card>
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <SectionLabel>Lapse Reason</SectionLabel>
              <select
                className={clsx(fieldClass, "cursor-pointer")}
                value={chase?.lapseReason ?? ""}
                onChange={(e) =>
                  UpdateChase(
                    { lapseReason: e.target.value },
                    "Lapse reason updated",
                  )
                }
                disabled={loading}
              >
                <option value="">No reason recorded…</option>
                {LAPSE_REASONS.map((reason) => (
                  <option key={reason.value} value={reason.value}>
                    {reason.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <SectionLabel>Payment Attempts</SectionLabel>
              <div
                className={clsx(
                  fieldClass,
                  "flex items-center justify-between gap-3",
                )}
              >
                <span className="text-[14px] font-semibold text-heading-color tabular-nums">
                  {chase?.paymentAttempts ?? 0}
                </span>
                <button
                  type="button"
                  onClick={() => runAction(attemptsAction(chase))}
                  disabled={actionLoading}
                  className="text-[12px] font-semibold text-blue cursor-pointer hover:underline disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  Update
                </button>
              </div>
            </div>
          </div>
        </Card>

        {/* The renewal pack — attached when the chase was marked as renewed.
            Links are minted per click, so the card only holds the record. */}
        {renewalDocuments.length ? (
          <Card>
            <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2">
              <SectionLabel>Renewal Documents</SectionLabel>
              <p className="text-[13px] text-light-gray">
                {renewalDocuments.length} attached
              </p>
            </div>

            <div className="mt-4 space-y-2">
              {renewalDocuments.map((doc) => (
                <button
                  key={doc.id}
                  type="button"
                  onClick={() => OpenDocument(doc.id)}
                  disabled={openingDoc === doc.id}
                  className="w-full flex items-center gap-3 text-left rounded-xl border border-edge bg-surface-muted/60 p-3 hover:border-indigo-400/40 hover:bg-active-background transition-colors duration-200 disabled:opacity-60 disabled:cursor-wait"
                >
                  <span className="w-9 h-9 shrink-0 rounded-lg flex items-center justify-center border border-edge bg-surface-elevated text-light-gray">
                    <FontAwesomeIcon
                      icon={openingDoc === doc.id ? faSpinner : faFileLines}
                      spin={openingDoc === doc.id}
                      className="text-sm"
                    />
                  </span>

                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] font-medium text-heading-color truncate">
                      {doc.name}
                    </p>
                    <p className="mt-0.5 text-[11px] text-light-gray truncate">
                      {doc.fileName} · {fileSize(doc.fileSize)}
                      {doc.createdAt
                        ? ` · ${moment(doc.createdAt).format("DD MMM YYYY")}`
                        : ""}
                    </p>
                  </div>

                  {doc.fee ? (
                    <span className="shrink-0 text-[13px] font-semibold text-heading-color tabular-nums">
                      {money(Number(doc.fee))}
                    </span>
                  ) : null}

                  <FontAwesomeIcon
                    icon={faArrowUpRightFromSquare}
                    className="shrink-0 text-xs text-light-gray"
                  />
                </button>
              ))}
            </div>
          </Card>
        ) : null}

        {/* Invite pricing — the quote sent plus the alternatives offered. */}
        <Card>
          <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2">
            <SectionLabel>Invite Pricing</SectionLabel>
            {chase?.coverType ? (
              <p className="text-[13px] text-light-gray">
                Policy Type:{" "}
                <span className="font-semibold text-heading-color">
                  {chase.coverType}
                </span>
              </p>
            ) : null}
          </div>

          {quotes.length ? (
            <div className="overflow-x-auto custom-scrollbar mt-5 -mx-1 px-1">
              {/* border-separate: collapsed borders ignore the rounded corners
                  on the highlighted column. */}
              <table className="w-full min-w-[560px] border-separate border-spacing-0">
                <thead>
                  <tr>
                    <th className="w-[170px]" />
                    {quotes.map((quote) => (
                      <th
                        key={quote.slot}
                        className={clsx(
                          "px-5 pt-3 pb-3.5 text-left align-bottom",
                          quote.isActive &&
                            "bg-active-background rounded-t-xl border-t-2 border-accent",
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className={clsx(
                              "text-[12px] font-bold uppercase tracking-[0.08em]",
                              quote.isActive ? "text-blue" : "text-light-gray",
                            )}
                          >
                            {QUOTE_SLOT_LABEL[quote.slot] ?? quote.slot}
                          </span>
                          {quote.isActive ? (
                            <span className="text-[9px] font-bold tracking-wider text-blue bg-surface border border-indigo-400/40 rounded-full px-2 py-0.5">
                              SENT
                            </span>
                          ) : null}
                        </div>
                        {quote.insurer ? (
                          <span className="block mt-1 text-[12px] font-medium text-light-gray normal-case">
                            {quote.insurer}
                          </span>
                        ) : null}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {QUOTE_LINE_FIELDS.map((field) => (
                    <tr key={field.key}>
                      <td className="text-[13px] text-light-gray py-2.5 pr-5">
                        {field.label}
                      </td>
                      {quotes.map((quote) => (
                        <td
                          key={quote.slot}
                          className={clsx(
                            "px-5 py-2.5 text-[14px] tabular-nums text-heading-color",
                            quote.isActive &&
                              "bg-active-background font-medium",
                          )}
                        >
                          {money(Number(quote[field.key] ?? 0))}
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr>
                    <td className="text-[13px] font-semibold text-heading-color py-3.5 pr-5 border-t border-edge">
                      Total
                    </td>
                    {quotes.map((quote) => (
                      <td
                        key={quote.slot}
                        className={clsx(
                          "px-5 py-3.5 text-[15px] font-bold tabular-nums border-t border-edge",
                          quote.isActive
                            ? "bg-active-background rounded-b-xl text-blue"
                            : "text-heading-color",
                        )}
                      >
                        {money(Number(quote.premium ?? 0))}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-[13px] text-light-gray mt-4">
              No quotes recorded.
            </p>
          )}
        </Card>

        <Card>
          <div className="flex items-center justify-between gap-4">
            <SectionLabel>Renewal Timeline</SectionLabel>
            <span className="text-[11px] font-semibold text-light-gray uppercase tracking-[0.14em] shrink-0">
              Amount
            </span>
          </div>

          <div className="mt-2">
            {logs?.data?.length ? (
              logs?.data?.map((item) => {
                return (
                  <div
                    className="flex items-start sm:items-center justify-between gap-4 py-3.5 border-b border-edge-faint last:border-none"
                    key={item?.id}
                  >
                    <h2 className="w-[86px] sm:w-[100px] text-[12px] font-semibold text-light-gray text-start shrink-0 tabular-nums">
                      {moment(item.createdAt).format("DD/MM/YYYY")}
                    </h2>

                    <h2 className="flex-1 min-w-0 text-[13px] sm:text-[14px] font-medium text-heading-color text-start capitalize break-words">
                      {item.type.replaceAll("-", " ")}{" "}
                      {item?.type === "chase-status-updated" ? (
                        <>
                          From
                          <span className="font-bold ml-[4px]">
                            {item?.data?.oldValues?.status}
                          </span>
                          <FontAwesomeIcon
                            icon={faArrowRightLong}
                            className="mx-2"
                          />
                          <span className="font-bold">
                            {item?.data?.newValues?.status}
                          </span>
                        </>
                      ) : null}
                      {item?.type === "chase-payment-attempts-updated" ? (
                        <>
                          <span className="font-bold ml-[4px]">
                            {item?.data?.oldValues?.paymentAttempts ?? 0}
                          </span>
                          <FontAwesomeIcon
                            icon={faArrowRightLong}
                            className="mx-2"
                          />
                          <span className="font-bold">
                            {item?.data?.newValues?.paymentAttempts}
                          </span>
                        </>
                      ) : null}
                      {item?.type === "version-reference-updated" ||
                      item?.type === "policy-reference-updated" ? (
                        <>
                          From
                          <span className="font-bold ml-[4px] normal-case">
                            {item?.data?.oldValue || "—"}
                          </span>
                          <FontAwesomeIcon
                            icon={faArrowRightLong}
                            className="mx-2"
                          />
                          <span className="font-bold normal-case">
                            {item?.data?.newValue}
                          </span>
                        </>
                      ) : null}
                      {item?.creator?.name ? (
                        <span className="text-light-gray normal-case">
                          {" "}
                          ({item?.creator?.name})
                        </span>
                      ) : null}
                    </h2>

                    <h2 className="w-[80px] sm:w-[96px] text-[13px] sm:text-[14px] font-semibold text-heading-color text-end shrink-0 tabular-nums">
                      {money(Number(item?.amount ?? 0))}
                    </h2>
                  </div>
                );
              })
            ) : (
              <p className="text-[13px] text-light-gray py-2">No events yet.</p>
            )}

            {/* {logs?.length >= 10 && page <= logs.totalCount ? (
                <div className="w-[90%] mx-auto flex items-center justify-end py-2">
                  <button
                    className="text-md text-heading-color cursor-pointer"
                    onClick={loadMoreLogs}
                    disabled={isFetching}
                  >
                    <FontAwesomeIcon
                      icon={faRotateRight}
                      className="text-md text-heading-color mr-[2px]"
                      spin={isFetching}
                    />
                    Load More
                  </button>
                </div>
              ) : null} */}
          </div>
        </Card>
      </div>
    </td>
  );
};

/** Panel card — one consistent surface, radius and padding for every section. */
const Card = ({ children }: { children: React.ReactNode }) => (
  <div className="w-full bg-surface rounded-2xl border border-edge p-5 sm:p-6 mt-4">
    {children}
  </div>
);

/** Small dotted caption used by the panel's cards. */
const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <p className="flex items-center gap-2 text-[11px] font-semibold text-light-gray uppercase tracking-[0.14em]">
    <span className="w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
    {children}
  </p>
);

export default ChaseDropMenu;
